/** The tax calendar (M07-03) and every return for the year, with alerts. */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, FilePlus2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';

export const FORM_SHORT: Record<string, string> = {
  F2550Q: '2550Q',
  F2551Q: '2551Q',
  F0619E: '0619-E',
  F1601EQ: '1601-EQ',
  QAP: 'QAP',
  SAWT: 'SAWT',
  SLSP: 'SLSP',
  F1604E: '1604-E',
  F1702Q: '1702Q',
  F1701Q: '1701Q',
  LGU_GROSS_SALES: 'LGU gross sales',
};
const STATUS_TONE = { NOT_STARTED: 'neutral', DRAFT: 'info', REVIEWED: 'warning', FILED: 'ok', SUPERSEDED: 'neutral' } as const;
const STATUS_LABEL = { NOT_STARTED: 'Not started', DRAFT: 'Draft', REVIEWED: 'Reviewed', FILED: 'Filed', SUPERSEDED: 'Superseded' } as const;

interface CalendarItem { form: string; label: string; year: number; period: number; periodLabel: string; dueDate: string; status: keyof typeof STATUS_TONE; overdue: boolean; dueSoon: boolean }
interface ReturnRow { id: string; form: string; year: number; period: number; version: number; status: keyof typeof STATUS_TONE; taxDue: string }
interface AlertRow { id: string; form: string; label: string; year: number; period: number; filedTaxDue: string; currentTaxDue: string }

export function TaxCenterPage(): JSX.Element {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [year, setYear] = useState(Number(manilaToday().slice(0, 4)));
  const [show, setShow] = useState<'open' | 'all'>('open');
  const [error, setError] = useState<string | null>(null);
  const calendar = useQuery({ queryKey: ['tax-calendar', year], queryFn: () => api<CalendarItem[]>(`/tax-returns/calendar?year=${year}`) });
  const returns = useQuery({ queryKey: ['tax-returns', year], queryFn: () => api<ReturnRow[]>(`/tax-returns?year=${year}`) });
  const alerts = useQuery({ queryKey: ['tax-alerts'], queryFn: () => api<AlertRow[]>('/tax-returns/amendment-alerts') });
  const generate = useMutation({
    mutationFn: (item: { form: string; year: number; period: number }) => api<{ id: string }>('/tax-returns', { method: 'POST', body: item }),
    onSuccess: (r) => navigate(`/tax/returns/${r.id}`),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not generate.'),
  });
  const find = (c: CalendarItem) => returns.data?.find((r) => r.form === c.form && r.period === c.period && r.year === c.year);
  const items = (calendar.data ?? []).filter((c) => show === 'all' || c.status !== 'FILED');

  return (
    <>
      <PageHeader title="Tax Center" description="Every BIR return you owe this year, built from posted documents. Generate, review, then mark filed with the eFPS or eBIRForms reference." actions={<Link to="/tax/checks"><Button variant="secondary">Pre-filing checks</Button></Link>} />
      {alerts.data && alerts.data.length > 0 && (
        <div className="mb-4">
          <Alert tone="warning">
            <span className="flex items-start gap-2"><AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>An amended return may be required.</strong> Postings after filing changed:{' '}
                {alerts.data.map((a) => <Link key={a.id} className="mr-3 underline" to={`/tax/returns/${a.id}`}>{FORM_SHORT[a.form]} {a.period ? `Q${a.period} ` : ''}{a.year} (filed {amount(a.filedTaxDue)}, now {amount(a.currentTaxDue)})</Link>)}
              </span>
            </span>
          </Alert>
        </div>
      )}
      {error && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      <FilterBar>
        <select className={filterClass} value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year">{[0, 1, 2].map((d) => Number(manilaToday().slice(0, 4)) - d).map((y) => <option key={y} value={y}>{y}</option>)}</select>
        <select className={filterClass} value={show} onChange={(e) => setShow(e.target.value as 'open' | 'all')} aria-label="Show"><option value="open">Not yet filed</option><option value="all">Everything</option></select>
      </FilterBar>
      <Card>
        {calendar.isLoading && <Skeleton className="h-64 w-full" />}
        {items.length === 0 && calendar.data && <EmptyState title="Everything is filed" />}
        {items.length > 0 && (
          <TableWrap>
            <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Due</Th><Th>Return</Th><Th>Period</Th><Th>Status</Th><Th align="right">Tax due</Th><Th>{''}</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((c) => {
                const r = find(c);
                return (
                  <tr key={`${c.form}-${c.period}`} className={c.overdue ? 'bg-red-50/60 dark:bg-red-950/30' : ''}>
                    <Td className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">{(c.overdue || c.dueSoon) && <CalendarClock aria-hidden="true" className={`h-4 w-4 ${c.overdue ? 'text-red-600' : 'text-amber-600'}`} />}{shortDate(c.dueDate)}</span>
                    </Td>
                    <Td><span className="font-medium">{FORM_SHORT[c.form]}</span><span className="block max-w-md truncate text-xs text-slate-500">{c.label}</span></Td>
                    <Td className="whitespace-nowrap">{c.periodLabel}</Td>
                    <Td><StatusChip tone={c.overdue ? 'critical' : STATUS_TONE[c.status]}>{c.overdue ? 'Overdue' : STATUS_LABEL[c.status]}</StatusChip></Td>
                    <Td align="right"><Money>{r ? amount(r.taxDue, true) : ''}</Money></Td>
                    <Td align="right">
                      {r ? <Link to={`/tax/returns/${r.id}`}><Button size="sm" variant="secondary">Open v{r.version}</Button></Link> : can('tax.manage') && (
                        <Button size="sm" busy={generate.isPending && generate.variables?.form === c.form && generate.variables?.period === c.period} onClick={() => generate.mutate({ form: c.form, year: c.year, period: c.period })}><FilePlus2 aria-hidden="true" className="h-4 w-4" /> Generate</Button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Card>
      <p className="mt-3 text-xs text-slate-500">Due dates follow the current rules as configured by ITCSI; confirm with your tax adviser each year. CoreX-lite prepares the figures and files; submission happens in eFPS or eBIRForms.</p>
    </>
  );
}
